import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { MonetizationType, OfferType, PresentationType, PromotedBundleEdge } from '@/@types/graphql-types';
import { formattedReleaseDate } from '@/helpers/date-helper';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { computed } from 'vue';
import { allowedLanguagesUpcomingReleases } from '@/features/buybox/helpers/upcoming-helpers';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { GetBuyBoxOffersQuery } from '@/features/buybox/graphql/queries/GetBuyBoxOffers.query';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import { buyboxFilterByMonetizationType, sortedPackageIds } from '@/features/buybox/helpers/list-buybox-config';
import type { BuyboxRegularOffer, UpcomingRelease } from '@/features/buybox/types/types';
import { includeGenericReleases } from '@/features/buybox/helpers/upcoming-buybox-utils';
import { sortJustWatchTVFirst } from '@/features/buybox/helpers/buyboxSorting';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { extractAVODOffer, extractBuyboxOffers } from '@/helpers/title-helper';
import { storeToRefs } from 'pinia';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import Provider from '@/interfaces/provider';
import { RentalListLocale, rentalListLocales } from '@/routing/lists';
import { useBuyRentExp } from '@/components/experiments/ExpBuyRent';
// EXP_LINEAR_TV
import { useExperiment } from '@/libraries/experiment';
// EXP_LINEAR_TV

export const FILTERABLE_BUYBOX_KEYS = ['buy', 'rent', 'flatrate', 'linear', 'free', 'dvdBluray'] as const;

export const AVOD_TECHNICAL_NAMES = {
	ACTIVE: ['plex', 'justwatchplexchannel'],
	INACTIVE: ['plexplayer', 'justwatchplexchannel'],
};

const sortingByFilterOption: Record<string, (a: TitleOfferFragment, b: TitleOfferFragment) => number> = {
	[BuyBoxFilterOptions.SUBSCRIPTION]: (offerA: TitleOfferFragment, offerB: TitleOfferFragment) => {
		const packageAId = String(offerA.package.packageId) as keyof typeof sortedPackageIds;
		const packageBId = String(offerB.package.packageId) as keyof typeof sortedPackageIds;

		const offerFreeTrialA = sortedPackageIds?.[packageAId]?.free_trial;
		const offerFreeTrialB = sortedPackageIds?.[packageBId]?.free_trial;

		const offerPriceA = sortedPackageIds?.[packageAId]?.price;
		const offerPriceB = sortedPackageIds?.[packageBId]?.price;

		// Sorts array by two dimensions - first the longest free trial length, then the cheapest price.
		return Number(offerFreeTrialB) - Number(offerFreeTrialA) || Number(offerPriceA) - Number(offerPriceB);
	},
	[BuyBoxFilterOptions.FREE]: sortJustWatchTVFirst,
	[BuyBoxFilterOptions.RENT]: sortJustWatchTVFirst,
};

export type BuyBoxOffers = AssertByTypename<
	NonNullable<GetBuyBoxOffersQuery['node']>,
	'Movie' | 'Show' | 'Season' | 'Episode'
>;

interface BuyBoxDataProps {
	title: MaybeRefOrGetter<TitleDetail>;
	buyboxData?: MaybeRefOrGetter<BuyBoxOffers | undefined | null>;
	buyboxFilter?: MaybeRefOrGetter<BuyBoxFilterOptions>;
	buyboxUpcomingReleases?: MaybeRefOrGetter<UpcomingRelease[]>;
	// EXP_PROVIDER_BUYBOX_V2
	providerFilter?: MaybeRefOrGetter<string[] | null>;
	// EXP_PROVIDER_BUYBOX_V2
}

function getDvdBluRayOffers(buyboxOffers: BuyBoxOffers): BuyboxRegularOffer[] {
	const titleDvdBluRayOffers =
		buyboxOffers.__typename === 'Show'
			? buyboxOffers.dvdBluray.map(offer => ({
					...offer,
					// Currently content don't have the season count correct for Shows & Seasons standard offers.
					// so once content fixes the elementCount issue for standard offers we can remove this.
					elementCount: offer.type === OfferType.Standard ? 0 : offer.elementCount,
			  }))
			: buyboxOffers.dvdBluray;

	// DVD offers are listed first, sorted by ascending price within the group.
	// Blu-ray offers follow, also sorted by ascending price within their group.

	const getGroupIndex = (type: PresentationType) =>
		type === PresentationType.Bluray || type === PresentationType.Bluray_4K ? 1 : 0;

	return [...titleDvdBluRayOffers].sort((a, b) => {
		const groupA = getGroupIndex(a.presentationType);
		const groupB = getGroupIndex(b.presentationType);

		if (groupA === groupB) {
			return (a.retailPriceValue || 0) - (b.retailPriceValue || 0);
		}
		return groupA - groupB;
	});
}

/**
 * Main composable for managing BuyBox data and filtering offers
 * @param buyboxData - BuyBox data object from GraphQL Query GetBuyBoxOffersQuery To override the default BuyboxOffers that got extracted from the title details.
 * @param title - Title details object
 * @param buyboxFilter - Current filter option selected by user (buyboxFilter in user store or  )
 * @param buyboxUpcomingReleases - upcoming releases for title or episodes buybox. Episodes are not fetched on the title query, so upcomingReleases need to be passed separately
 */
export function useBuyBoxData({
	buyboxData,
	title,
	buyboxFilter,
	buyboxUpcomingReleases = () => [] as UpcomingRelease[],
	// EXP_PROVIDER_BUYBOX_V2
	providerFilter,
}: // EXP_PROVIDER_BUYBOX_V2
BuyBoxDataProps) {
	const { allProvidersByShortName } = storeToRefs(useConstantStore());
	const { country, language, webLocale } = storeToRefs(useLanguageStore());
	const { isActive: isInternationalOffersActive } = useInternationalOffersStatus({ title });

	const { filterBuyOffersBuyRentExp } = useBuyRentExp({ title });

	// EXP_LINEAR_TV
	const { activeVariant: linearTVVariant } = useExperiment('EXP_LINEAR_TV', 'local');
	const isLinearTVVariant1 = computed(() => linearTVVariant.value === 'variant_1');
	const isLinearTVVariant2 = computed(() => linearTVVariant.value === 'variant_2');
	const showLiveTvChip = computed(() => isLinearTVVariant1.value || isLinearTVVariant2.value);
	// EXP_LINEAR_TV

	const currentBuyboxFilter = computed(() => toValue(buyboxFilter) ?? BuyBoxFilterOptions.ALL);

	// EXP_PROVIDER_BUYBOX_V2
	const currentProviderFilter = computed(() => toValue(providerFilter) ?? null);
	// EXP_PROVIDER_BUYBOX_V2

	const currentTitle = computed(() => toValue(title));

	const currentOffers = computed(() => {
		const buyboxDataValue = toValue(buyboxData) ?? (currentTitle.value && extractBuyboxOffers(currentTitle.value));

		if (
			buyboxDataValue?.__typename === 'Episode' ||
			buyboxDataValue?.__typename === 'Movie' ||
			buyboxDataValue?.__typename === 'Season' ||
			buyboxDataValue?.__typename === 'Show'
		) {
			const groupedBuyboxOffers = groupBuyboxOffersByMonetization(buyboxDataValue);
			const {
				buy,
				rent,
				linear: linearWithFlatrateAndFree,
				free,
				flatrate,
			} = filterInternationalOffers(groupedBuyboxOffers, country.value, isInternationalOffersActive.value);

			// EXP_LINEAR_TV: linear_free/linear_flatrate offers only exist for variant_1/variant_2
			const linear = showLiveTvChip.value
				? linearWithFlatrateAndFree
				: linearWithFlatrateAndFree.filter(
						offer =>
							![MonetizationType.LinearFree, MonetizationType.LinearFlatrate].includes(
								offer.monetizationType
							)
				  );
			// EXP_LINEAR_TV

			const bundles = filterBundlesForTimVision(
				currentTitle.value,
				(buyboxDataValue.bundles ?? []) as PromotedBundleEdge[],
				country.value
			);

			const res = {
				rent,
				buy,
				free,
				linear,
				bundles,
				cinema: [] as TitleOfferFragment[],
				stream: [] as TitleOfferFragment[],
				promotedOffers: [] as BuyboxRegularOffer[],
				dvdBluRayOffers: getDvdBluRayOffers(buyboxDataValue),
			};
			if (buyboxDataValue.__typename !== 'Episode') {
				const promotedBundles = formatBundleOffers(buyboxDataValue.promotedBundles as PromotedBundleEdge[]);

				res.promotedOffers = buyboxDataValue.promotedOffers.concat(promotedBundles).filter(
					// Do not promote JWTV on AVOD titles
					// This is because the CMS doesn't support promotion based on monetization type
					offer => offer.package.shortName !== 'jwt' || offer.monetizationType === MonetizationType.Rent
				);
			}
			flatrate.forEach(offer =>
				offer.monetizationType === MonetizationType.Cinema ? res.cinema.push(offer) : res.stream.push(offer)
			);

			return res;
		}

		return {
			stream: [],
			rent: [],
			buy: [],
			free: [],
			linear: [],
			cinema: [],
			bundles: [],
			promotedOffers: [],
			dvdBluRayOffers: [],
		};
	});

	const AVODOffer = computed(() => (currentTitle.value ? extractAVODOffer(currentTitle.value) : null));

	const dvdBluRayOffers = computed(() => {
		return currentOffers.value.dvdBluRayOffers;
	});

	const offersByFilterOption = computed<Record<string, TitleOfferFragment[]>>(() => {
		const AVODTechnicalName = AVODOffer.value ? AVOD_TECHNICAL_NAMES.ACTIVE : AVOD_TECHNICAL_NAMES.INACTIVE;

		// HIDE OTT Studio Channel from regular free offers when Player offer is present
		if (currentOffers.value.free.some(offer => offer.package.technicalName === 'ottstudioplayer')) {
			AVODTechnicalName.push('ottstudiochannel');
		}

		const subscriptionOffers = [...currentOffers.value.stream].sort((a, b) =>
			sortingByFilterOption[BuyBoxFilterOptions.SUBSCRIPTION](a, b)
		);

		return {
			[BuyBoxFilterOptions.SUBSCRIPTION]: subscriptionOffers,
			[BuyBoxFilterOptions.FREE]: [...currentOffers.value.free]
				.filter(el => !AVODTechnicalName.includes(el.package.technicalName))
				.sort((a, b) => sortingByFilterOption[BuyBoxFilterOptions.FREE](a, b)),
			// EXP_LINEAR_TV
			// only one of FAST_TV (control) / LINEAR_TV (variant_1 & variant_2) is populated at a time — both keys pointing at the same offers would double-count them in allRegularOffers (Object.values(...).flat())
			[BuyBoxFilterOptions.FAST_TV]: showLiveTvChip.value ? [] : currentOffers.value.linear,
			[BuyBoxFilterOptions.LINEAR_TV]: showLiveTvChip.value ? currentOffers.value.linear : [],
			// EXP_LINEAR_TV
			[BuyBoxFilterOptions.RENT]: currentOffers.value.rent
				.filter(
					offer =>
						![PresentationType.Bluray, PresentationType.Dvd, PresentationType.Bluray_4K].includes(
							offer.presentationType
						)
				)
				.sort((a, b) => sortingByFilterOption[BuyBoxFilterOptions.RENT](a, b)),
			[BuyBoxFilterOptions.BUY]: currentOffers.value.buy.filter(
				offer =>
					![PresentationType.Bluray, PresentationType.Dvd, PresentationType.Bluray_4K].includes(
						offer.presentationType
					)
			),
			[BuyBoxFilterOptions.CINEMA]: currentOffers.value.cinema,
		};
	});

	// all regular offers (non-bundle offers + non DVD/BluRay offers)
	const allRegularOffers = computed<TitleOfferFragment[]>(() => {
		return Object.values(offersByFilterOption.value).flat();
	});

	// Check if any non-cinema and non-physical (DVD, Blu-Ray etc.) offers exist
	const hasVODOffers = computed(() => {
		return allRegularOffers.value.some(offer => offer.monetizationType !== MonetizationType.Cinema);
	});

	const promotedOffers = computed(() => {
		return currentOffers.value.promotedOffers;
	});

	const filteredRegularOffers = computed(() => {
		let bundles = formatBundleOffers(currentOffers.value.bundles as PromotedBundleEdge[]);

		let regularOffers = [] as BuyboxRegularOffer[];
		if (
			currentBuyboxFilter.value === BuyBoxFilterOptions.ALL ||
			// EXP_PROVIDER_BUYBOX_V2: My Services has no dedicated offer bucket - it narrows all regular offers down
			// to the selected providers below, so it needs to start from the same unfiltered set as ALL.
			currentBuyboxFilter.value === BuyBoxFilterOptions.MY_SERVICES
		) {
			// EXP MERGE BUY RENT
			regularOffers = allRegularOffers.value.filter(offer => !filterBuyOffersBuyRentExp(offer));
			// EXP MERGE BUY RENT
		} else if (currentBuyboxFilter.value === BuyBoxFilterOptions.SUBSCRIPTION) {
			regularOffers = offersByFilterOption.value[BuyBoxFilterOptions.SUBSCRIPTION];
		} else if (currentBuyboxFilter.value === BuyBoxFilterOptions.FREE) {
			regularOffers = offersByFilterOption.value[BuyBoxFilterOptions.FREE];
			// EXP_LINEAR_TV
			if (isLinearTVVariant1.value || isLinearTVVariant2.value) {
				regularOffers = [
					...regularOffers,
					...currentOffers.value.linear.filter(offer =>
						[MonetizationType.Fast, MonetizationType.LinearFree].includes(offer.monetizationType)
					),
				];
			}
			// EXP_LINEAR_TV
		} else {
			regularOffers = offersByFilterOption.value[currentBuyboxFilter.value] ?? [];
		}

		// EXP_PROVIDER_BUYBOX_V2
		if (currentProviderFilter.value !== null) {
			regularOffers = regularOffers.filter(offer =>
				currentProviderFilter.value!.includes(offer.package.shortName)
			);
			bundles = bundles.filter(bundle =>
				bundle.icons?.some(icon => currentProviderFilter.value!.includes(icon.shortName))
			);
		}
		// EXP_PROVIDER_BUYBOX_V2

		// Filter physical offers based on current filter
		const filteredDvdBluRayOffers = dvdBluRayOffers.value.filter(offer => {
			if (currentBuyboxFilter.value === BuyBoxFilterOptions.ALL) {
				// Physical will be hidden via CSS if VOD exists
				return true;
			}
			// Only show physical offers in the Disc tab (not in Buy or Rent)
			if (currentBuyboxFilter.value === BuyBoxFilterOptions.PHYSICAL) {
				return (buyboxFilterByMonetizationType[currentBuyboxFilter.value] ?? []).includes(
					offer.monetizationType
				);
			}
			return false;
		});

		if (
			currentBuyboxFilter.value === BuyBoxFilterOptions.ALL ||
			currentBuyboxFilter.value === BuyBoxFilterOptions.MY_SERVICES
		) {
			const cinemaOffers = regularOffers.filter(offer => offer.monetizationType === MonetizationType.Cinema);
			// EXP_LINEAR_TV: linear offers are pushed to the bottom, right before physical (DVD/Blu-ray) offers
			const linearOffers = regularOffers.filter(offer =>
				buyboxFilterByMonetizationType[BuyBoxFilterOptions.LINEAR_TV].includes(offer.monetizationType)
			);
			const digitalOffers = regularOffers.filter(
				offer => offer.monetizationType !== MonetizationType.Cinema && !linearOffers.includes(offer)
			);

			// Digital, then bundles, then cinema, then linear, then physical (DVD→Blu-ray by price) at the bottom
			return [...digitalOffers, ...bundles, ...cinemaOffers, ...linearOffers, ...filteredDvdBluRayOffers];
		}

		// For other filters (keep existing logic)
		return [BuyBoxFilterOptions.SUBSCRIPTION].includes(currentBuyboxFilter.value)
			? regularOffers.concat(bundles).concat(filteredDvdBluRayOffers)
			: regularOffers.concat(filteredDvdBluRayOffers);
	});

	const upcomingReleases = computed(() => {
		if (!allowedLanguagesUpcomingReleases.includes(language.value)) return [];

		const offersProviderShortNames = allRegularOffers.value.map(offer => offer.package.shortName);

		const upcomingReleases = toValue(buyboxUpcomingReleases);

		const upcomingReleasesFiltered = upcomingReleases
			.filter(release => {
				// TEMP: this excludes showing free JWTV Free Offers as upcoming for countries which have rental titles, while waiting for a proper fix from Content.
				return !(
					release.package?.shortName === 'jwt' &&
					rentalListLocales.includes(webLocale.value as RentalListLocale)
				);
			})
			.filter(release => {
				return (
					!!release.package &&
					!!allProvidersByShortName.value[release.package.shortName as keyof Provider] &&
					!offersProviderShortNames.includes(release.package.shortName)
				);
			});

		const hasDigitalOffers =
			allRegularOffers.value.filter(offer => offer.monetizationType !== MonetizationType.Cinema).length > 0;
		const hasCinemaOffers =
			allRegularOffers.value.filter(offer => offer.monetizationType === MonetizationType.Cinema).length > 0;

		// if no upcoming digital releases, includes generic digital release. If no upcoming theatrical releases, includes generic theatrical release.
		return includeGenericReleases(upcomingReleases, upcomingReleasesFiltered, hasCinemaOffers, hasDigitalOffers);
	});

	const upcomingReleasesFromGroups = computed(() => {
		const uniqueReleases = new Set();
		const groupedReleases: Record<string, UpcomingRelease[]> = {};

		// Step 1: Group filtered releases
		upcomingReleases.value
			.filter(el =>
				showUpcomingRelease(el, currentBuyboxFilter.value, hasVODOffers.value, currentProviderFilter.value)
			)
			.forEach((release, index) => {
				const uniqueKey = release.package
					? `${release.package.packageId}-${release.package.monetizationTypes[0]}`
					: index.toString();

				if (!uniqueReleases.has(uniqueKey)) {
					uniqueReleases.add(uniqueKey);
					const groupKey = buildGroupKey(release, index, language.value, country.value);
					if (!groupedReleases[groupKey]) {
						groupedReleases[groupKey] = [];
					}
					groupedReleases[groupKey].push(release);
				}
			});

		// Step 2: Transform and sort groups
		return (
			Object.values(groupedReleases)
				// Sort each group by monetization type
				// Transform into final format with group metadata
				.flatMap(group => {
					const allSameProviders = group.every(
						(release, _, releases) => release.package?.shortName === releases[0].package?.shortName
					);
					return group.map((item, index) => ({
						offer: item,
						isInGroup: group.length > 1,
						isLastOfGroup: group.length > 1 && index === group.length - 1,
						isFirst: index === 0,
						allSameProviders,
					}));
				})
		);
	});

	return {
		AVODOffer,
		filteredRegularOffers,
		allRegularOffers,
		upcomingReleasesFromGroups,
		upcomingReleases,
		promotedOffers,
		dvdBluRayOffers,
		isInternationalOffersActive,
		hasVODOffers,
	};
}

function showUpcomingRelease(
	release: UpcomingRelease,
	buyboxFilter: BuyBoxFilterOptions,
	hasVOD: boolean,
	providerFilter: string[] | null
) {
	if (buyboxFilter === BuyBoxFilterOptions.ALL) {
		// Only exclude cinema when VOD exists
		// Keep Buy/Rent upcoming releases visible (we can't distinguish physical vs digital yet)
		if (release.releaseType === 'THEATRICAL' && hasVOD) {
			return false;
		}

		return true;
	}
	if (release.releaseType === 'THEATRICAL') return buyboxFilter === BuyBoxFilterOptions.CINEMA;

	// Don't show upcoming releases under Disc (we can't confirm if Buy/Rent upcoming releases are physical without a presentationType)
	if (buyboxFilter === BuyBoxFilterOptions.PHYSICAL) {
		return false;
	}

	// EXP_PROVIDER_BUYBOX_V2: only show upcoming releases from the selected providers, same as regular offers
	if (buyboxFilter === BuyBoxFilterOptions.MY_SERVICES) {
		return !!providerFilter && !!release.package && providerFilter.includes(release.package.shortName);
	}
	// EXP_PROVIDER_BUYBOX_V2

	// For all other filters (Buy, Rent, Subscription, Free, etc.), use monetization type matching
	const monetizationType = release.package?.monetizationTypes[0];
	return buyboxFilterByMonetizationType[buyboxFilter].includes(monetizationType as MonetizationType);
}
// Generate a unique key for grouping upcoming releases
function buildGroupKey(release: UpcomingRelease, index: number, language: string, country: string) {
	const releaseDate = release.releaseDate
		? formattedReleaseDate(release.releaseDate, release.label, language, 'short', country).heading
		: index.toString();

	return `${releaseDate}_${release.releaseType}`;
}

type MonetizationGroupedBuyboxOffers = ReturnType<typeof groupBuyboxOffersByMonetization>;
function groupBuyboxOffersByMonetization(titleDetailNode: BuyBoxOffers) {
	type KeysToFilter = (typeof FILTERABLE_BUYBOX_KEYS)[number];

	return Object.fromEntries(FILTERABLE_BUYBOX_KEYS.map(key => [key, titleDetailNode[key]])) as {
		[K in KeysToFilter]: TitleOfferFragment[];
	};
}

function filterInternationalOffers(
	groupedBuyboxOffers: MonetizationGroupedBuyboxOffers,
	country: string,
	isInternationalOffersActive: boolean
): MonetizationGroupedBuyboxOffers {
	if (!isInternationalOffersActive) {
		// filter out offers from a foreign country, if international offers is not active.
		// this is needed because we query our offers by monetization type.
		// and if we for instance have a domestic offer in FLATRATE monetization type but RENT has none, then the RENT offers will fallback to include international offers.
		// as soon as we use only 1 offers prop in the query, not split up in monetization types, then this FE filtering isn't needed anymore.
		return Object.keys(groupedBuyboxOffers).reduce((root, key) => {
			const typedKey = key as keyof typeof groupedBuyboxOffers;
			root[typedKey] = groupedBuyboxOffers[typedKey].filter(offer => offer.country === country);
			return root;
		}, {} as any);
	}

	return {
		...groupedBuyboxOffers,
		// filter out cinema offers if we have international offers active
		flatrate: groupedBuyboxOffers.flatrate.filter(offer => offer.monetizationType !== MonetizationType.Cinema),
	};
}

function formatBundleOffers(bundles: PromotedBundleEdge[] | undefined): BuyboxRegularOffer[] {
	return (bundles ?? []).map(bundle => ({
		...bundle.offer,
		standardWebURL: bundle.promotionUrl,
		bundleClearName: bundle.node.clearName,
		bundleId: bundle.node.bundleId,
		bundleTechnicalName: bundle.node.technicalName,
		bundleShortName: bundle.node.shortName,
		icons: bundle.node.packages.map(pkg => ({ iconWide: pkg.iconWide, shortName: pkg.shortName })),
		offerEpisodes: bundle.offer.offerEpisodes ?? undefined,
	}));
}

function filterBundlesForTimVision(title: TitleDetail, bundles: PromotedBundleEdge[] | undefined, country: string) {
	if (!bundles || bundles.length === 0) return bundles;
	const hasTimVisionOffer = bundles.some(bundle => bundle.node.bundleId === 1066);

	if (country !== 'IT' || !hasTimVisionOffer) return bundles;

	const timVisionOffers = title.offers.filter(offer => offer.package.shortName === 'tvi');

	if (timVisionOffers.length === 0) return bundles;

	return timVisionOffers.some(offer => offer.monetizationType === MonetizationType.Flatrate)
		? bundles
		: bundles.filter(bundle => bundle.node.bundleId !== 1066);
}
